/* NOTE(shared-lib): this Places address-parsing logic was ported into web/canvas
 * (src/hooks/useAutocompleteSuggestions.ts) for Initiate Knox Verify. Keep in sync until consolidated
 * into a shared web package. */
import { useCallback } from 'react';
import { useLocationRestrictions, LocationRestriction } from './useLocationRestrictions';
import logger from '@app/libs/logger';

export const STARTS_WITH_DIGIT_REGEX = /^\d/;

export interface AutocompleteSuggestion {
  placePrediction: {
    id: string;
    mainText: {
      text: string;
    };
    secondaryText?: {
      text: string;
    };
  };
  description?: string;
}

export interface AddressComponents {
  streetAddress?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  formattedAddress?: string;
}

export const useAutocompleteSuggestions = (): {
  fetchPredictions: (
    input: string,
    city?: string,
    state?: string,
    types?: string[],
    componentRestrictions?: { country: string },
  ) => Promise<AutocompleteSuggestion[]>;
  getPlaceDetails: (suggestion: AutocompleteSuggestion) => Promise<AddressComponents | null>;
} => {
  const { getLocationRestrictions } = useLocationRestrictions();

  const getAutocompleteSuggestions = useCallback(
    async (
      input: string,
      locationRestriction: LocationRestriction | undefined,
      types?: string[],
      componentRestrictions?: { country: string },
    ): Promise<AutocompleteSuggestion[]> => {
      try {
        if (!window.google?.maps?.places) return [];

        const { suggestions } = await (window.google.maps.places as any).AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
          includedPrimaryTypes: types || ['route', 'street_address', 'administrative_area_level_1'],
          includedRegionCodes: componentRestrictions?.country ? [componentRestrictions.country] : undefined,
          locationRestriction: locationRestriction,
        });

        return suggestions || [];
      } catch (error) {
        logger.exception(error, { input, locationRestriction });
        return [];
      }
    },
    [],
  );

  const fetchPredictions = useCallback(
    async (
      input: string,
      city?: string,
      state?: string,
      types?: string[],
      componentRestrictions?: { country: string },
    ): Promise<AutocompleteSuggestion[]> => {
      const locationRestriction = await getLocationRestrictions(city, state, componentRestrictions);
      const autocompleteSuggestions = await getAutocompleteSuggestions(input, locationRestriction, types, componentRestrictions);
      return autocompleteSuggestions;
    },
    [getLocationRestrictions, getAutocompleteSuggestions],
  );

  const getPlaceDetails = useCallback(async (suggestion: AutocompleteSuggestion): Promise<AddressComponents | null> => {
    try {
      if (!window.google?.maps?.places) {
        console.error('Google Maps Places API not available');
        return null;
      }

      // Use the toPlace() method from the placePrediction object
      let place: any;
      if (typeof (suggestion.placePrediction as any).toPlace === 'function') {
        place = (suggestion.placePrediction as any).toPlace();
      } else if (typeof (suggestion as any).toPlace === 'function') {
        place = (suggestion as any).toPlace();
      } else if ((suggestion.placePrediction as any).placeId) {
        place = new (window.google.maps.places as any).Place({ id: (suggestion.placePrediction as any).placeId });
      } else if ((suggestion.placePrediction as any).place) {
        place = (suggestion.placePrediction as any).place;
      } else {
        console.error('Cannot find place or placeId in suggestion');
        return null;
      }

      await place.fetchFields({
        fields: ['addressComponents', 'formattedAddress'],
      });

      const addressComponents: AddressComponents = {
        formattedAddress: place.formattedAddress,
      };

      // Parse address components
      if (place.addressComponents) {
        // City priority order (higher priority = lower number)
        // We need to check all components first to find the highest priority city type
        const cityCandidates: Array<{ value: string; priority: number }> = [];
        let streetNumber = '';
        let route = '';

        place.addressComponents.forEach((component: any) => {
          const types = component.types;
          const value = component.longText || component.shortText;

          if (types.includes('street_number')) {
            streetNumber = value;
          }

          if (types.includes('route')) {
            route = value;
          }

          // Collect city candidates with priority
          // Priority order: locality (1) > postal_town (2) > sublocality_level_1 (3) >
          // administrative_area_level_3 (4) > administrative_area_level_2 (5)
          // A component may have multiple types, so we find the highest priority one for this component
          let componentCityPriority = Infinity;
          if (types.includes('locality')) {
            componentCityPriority = Math.min(componentCityPriority, 1);
          }
          if (types.includes('postal_town')) {
            componentCityPriority = Math.min(componentCityPriority, 2);
          }
          if (types.includes('sublocality_level_1')) {
            componentCityPriority = Math.min(componentCityPriority, 3);
          }
          if (types.includes('administrative_area_level_3')) {
            componentCityPriority = Math.min(componentCityPriority, 4);
          }
          if (types.includes('administrative_area_level_2')) {
            componentCityPriority = Math.min(componentCityPriority, 5);
          }
          if (componentCityPriority !== Infinity) {
            cityCandidates.push({ value, priority: componentCityPriority });
          }

          if (types.includes('administrative_area_level_1')) {
            addressComponents.state = value;
          }
          if (types.includes('country')) {
            addressComponents.country = value;
          }
          if (types.includes('postal_code')) {
            addressComponents.postalCode = value;
          }
        });

        if (streetNumber && route) {
          addressComponents.streetAddress = `${streetNumber} ${route}`;
        } else {
          const mainText = suggestion.placePrediction?.mainText?.text;
          if (mainText && STARTS_WITH_DIGIT_REGEX.test(mainText)) {
            addressComponents.streetAddress = mainText;
          } else {
            addressComponents.streetAddress = route;
          }
        }

        // Select the city with the highest priority (lowest priority number)
        if (cityCandidates.length > 0) {
          cityCandidates.sort((a, b) => a.priority - b.priority);
          addressComponents.city = cityCandidates[0].value;
        }
      }

      return addressComponents;
    } catch (error) {
      console.error('Error in getPlaceDetails:', error);
      logger.exception(error, { suggestion });
      return null;
    }
  }, []);

  return { fetchPredictions, getPlaceDetails };
};
